//윈터스쿨 결제링크 전송하기 모달 — 디자인(winter.html 관리자 모달 "결제링크 전송하기")을 React 로 옮김
//금액 계산은 ../functions/winterprice (서버·고객 /winterpay 와 같은 코드)만 사용한다. 여기서 계산식을 다시 만들지 말 것.
//금액표·기간·혜택·프로그램·지점은 서버 GET /dashboard/payments/winterpay/staffconfig 에서 받는다 (설정 화면은 다른 곳).
import React, { CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import WinterEnrolledPickerModal, { EnrolledStudent, studentLabel } from "./winterenrolledpickermodal";
import { Modal } from "@mui/joy";
import { toast } from "react-toastify";
import { getFetch, postFetch } from "../../../../../fetch/fetch";
import {
    calcRate, rateSegments, prorateBreakdown, benefitMet, dayNum, toDate, fromDayNum, ymd, isYmd,
    WinterRates, WinterProgram, WinterBranch, WinterBenefit,
} from "../functions/winterprice";

// ===== 상수 · 보조 함수 (디자인 스크립트 그대로) =====
const ACC = "#1976D2", ACC_SOFT = "#E3F2FD"; // 강조색 (달력과 통일)
const INVALID = "#D92D20";
const CAL = { band: "#90CAF9", accent: "#1976D2" };
const SEAT_MAX = 500; // 자리 번호 1 ~ 500
const WK = ["일", "월", "화", "수", "목", "금", "토"];
const TYPES: { key: "general" | "student"; label: string }[] = [
    { key: "general", label: "N수/자퇴" },
    { key: "student", label: "재학생" },
];
const pad2 = (n: number) => String(n).padStart(2, "0");
const digits = (v: any) => String(v ?? "").replace(/\D/g, "");
const fmtTel = (v: any) => { const d = digits(v).slice(0, 11); return d.length > 7 ? d.slice(0, 3) + "-" + d.slice(3, 7) + "-" + d.slice(7) : d.length > 3 ? d.slice(0, 3) + "-" + d.slice(3) : d; };
const comma = (v: any) => (v === "" || v == null ? "" : Number(v).toLocaleString("ko-KR"));
const won = (v: number) => v.toLocaleString("ko-KR") + "원";
const mdKo = (str: string) => { if (!isYmd(str)) return "(날짜)"; const d = toDate(str); return (d.getMonth() + 1) + "월 " + d.getDate() + "일"; };
const daysIn = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
const segDays = (g: { start: Date; end: Date }) => dayNum(g.end) - dayNum(g.start) + 1;
const periodsText = (ids: string[], periods: WinterRates["periods"]) => {
    if (!ids.length) return "등록 기간 전체";
    return ids.map(id => { const p = periods.find(x => x.id === id); return p ? (String(p.name).trim() || "(이름 없음)") : null; }).filter(Boolean).join("·");
};
const condText = (c: WinterBenefit) => (c.type === "always" ? "조건 없음" : c.type === "until" ? "결제일 " + mdKo(c.dates[0]) + "까지" : "결제일 " + c.dates.map(mdKo).join(", "));
// 기간 날짜 구간 → [시작 dayNum, 종료 dayNum] 목록
const rangesOf = (list: { start: string; end: string }[] | undefined) => (list || [])
    .filter(r => isYmd(r.start) && isYmd(r.end) && dayNum(toDate(r.start)) <= dayNum(toDate(r.end)))
    .map(r => [dayNum(toDate(r.start)), dayNum(toDate(r.end))] as [number, number]);
const fmtLong = (d: Date) => d.getFullYear() + "년 " + pad2(d.getMonth() + 1) + "월 " + pad2(d.getDate()) + "일(" + WK[d.getDay()] + "요일)";

interface StaffConfig {
    rates: WinterRates;
    programs: WinterProgram[];
    branches: WinterBranch[];
    seatRandomPid: string;
    today: string; // 서버 기준 오늘 "YYYY-MM-DD"
}

// ===== 윈터 날짜 규칙 (디자인 progWin/inProg/winterProg/winterZone/winterOk/winterEnds) =====
// 시작 가능 구간 = 금액표 기간(rates.periods)의 그 프로그램 날짜. 없으면 프로그램 startFrom~startTo
const progWin = (cfg: StaffConfig, p: WinterProgram): [number, number] => {
    const per = (cfg.rates.periods || []).find(x => x.id === p.pid);
    const rs = per && Array.isArray(per.ranges) ? per.ranges.filter(r => isYmd(r.start) && isYmd(r.end)) : [];
    if (rs.length) return [Math.min(...rs.map(r => dayNum(toDate(r.start)))), Math.max(...rs.map(r => dayNum(toDate(r.end))))];
    return [dayNum(toDate(p.startFrom)), dayNum(toDate(p.startTo))];
};
const inProg = (cfg: StaffConfig, p: WinterProgram, n: number) => { const [a, z] = progWin(cfg, p); return n >= a && n <= z; };
// 시작일 n 이 속한 프로그램 (고정 시작 지점은 고정일만)
const winterProg = (cfg: StaffConfig, branch: string, n: number) =>
    cfg.programs.find(p => inProg(cfg, p, n)
        && !(p.fixedStart && p.fixedStart.branches.includes(branch) && n !== dayNum(toDate(p.fixedStart.date)))) || null;
const winterZone = (cfg: StaffConfig, n: number) => cfg.programs.some(p => inProg(cfg, p, n));
// 시작한 프로그램 + 이어지는 뒤 프로그램의 종료일까지 허용 (중복 제거)
const winterEnds = (cfg: StaffConfig, branch: string, st: number): string[] => {
    const p = winterProg(cfg, branch, st);
    if (!p) return [];
    const all = cfg.programs.slice(cfg.programs.indexOf(p)).flatMap(x => x.ends || []).filter(isYmd);
    return Array.from(new Set(all));
};
const winterOk = (cfg: StaffConfig, branch: string, start: Date | null, n: number, role: "start" | "end", startN?: number) => {
    if (role === "start") return !!winterProg(cfg, branch, n);
    const st = startN != null ? startN : (start ? dayNum(start) : null);
    if (st == null) return false;
    return winterEnds(cfg, branch, st).some(e => dayNum(toDate(e)) === n && n >= st);
};

// ===== 화면 상태 =====
interface S {
    name: string; gender: "" | "male" | "female"; phone: string; branch: string; seat: string; hours: string;
    days: string; // 재학생 모드: 결제선생 기한(일)
    students: EnrolledStudent[]; pickerOpen: boolean; moreOpen: boolean; // 재학생 모드: 고른 재원생·고르기 팝업·더보기
    type: "general" | "student" | null;
    start: Date | null; end: Date | null;
    checks: { [id: string]: boolean };
    manual: string; editing: boolean;
    split: boolean; splitN: number; splitAuto: boolean; parts: string[];
    optOpen: boolean; openDd: string | null;
    viewY: number; viewM: number;
    stmtOpen: boolean; confirmOpen: boolean;
    tipKey: string | null;
}
// 사람마다 다른 값은 미리 채우지 않음 (디자인 state 초기값)
const initialState = (): S => ({
    name: "", gender: "", phone: "010", branch: "", seat: "", hours: "5",
    days: "7", students: [], pickerOpen: false, moreOpen: false,
    type: null, start: null, end: null, checks: {},
    manual: "", editing: false, split: false, splitN: 2, splitAuto: true, parts: [],
    optOpen: false, openDd: null,
    viewY: new Date().getFullYear(), viewM: new Date().getMonth(),
    stmtOpen: false, confirmOpen: false, tipKey: null,
});
// 기간이 바뀌면 수동 금액·분할 금액 초기화 (디자인 공통 패치)
const RESET_COST: Partial<S> = { manual: "", editing: false, splitAuto: true, parts: [] };

// '○○부터' 버튼 원본: '전체' 대상 기간 중 안 끝난 것 (시작일은 오늘 이후·일요일 제외)
const presetSources = (cfg: StaffConfig, todayN: number) => cfg.rates.periods
    .filter(p => p.target === "all")
    .map(p => {
        const r = rangesOf(p.ranges); if (!r.length) return null;
        const a = Math.min(...r.map(x => x[0])), z = Math.max(...r.map(x => x[1]));
        if (z < todayN) return null;
        let n = Math.max(a, todayN); if (fromDayNum(n).getDay() === 0) n++; // 진행 중이면 오늘부터 · 일요일 시작 불가
        return n <= z ? { name: String(p.name).trim(), a, z, n } : null; // 일요일로 밀려 종료일을 넘으면 버튼 숨김
    })
    .filter((x): x is { name: string; a: number; z: number; n: number } => !!x)
    .sort((x, y) => x.a - y.a);

// ===== 공통 스타일 =====
const ROOT_VARS = {
    "--ink-900": "#191F28", "--ink-700": "#333D4B", "--ink-500": "#6B7684", "--ink-400": "#8B95A1",
    "--ink-300": "#B0B8C1", "--ink-200": "#E5E8EB", "--hair": "#E5E8EB", "--bg-soft": "#F2F4F6", "--bg-canvas": "#F9FAFB",
    "--font-sans": "'Pretendard Variable', 'Apple SD Gothic Neo', sans-serif",
} as CSSProperties;
const FONT = "'Pretendard Variable', 'Apple SD Gothic Neo', sans-serif";
const labelSt: CSSProperties = { fontSize: 15, fontWeight: 500, color: "var(--ink-900)" };
const col = (gap: number): CSSProperties => ({ display: "flex", flexDirection: "column", gap });
const inputSt: CSSProperties = { height: 48, padding: "0 16px", border: "1px solid var(--ink-200)", borderRadius: 8, fontFamily: "inherit", fontSize: 15, color: "var(--ink-900)", letterSpacing: "-0.02em", background: "#fff", boxSizing: "border-box", width: "100%" };
const segSt = (active: boolean): CSSProperties => ({ height: 48, width: "100%", borderRadius: 8, border: "1px solid " + (active ? ACC : "var(--ink-200)"), background: active ? ACC_SOFT : "#fff", color: active ? ACC : "var(--ink-900)", fontFamily: "inherit", fontSize: 15, fontWeight: 500, letterSpacing: "-0.02em", cursor: "pointer", padding: "0 12px" });
const grid2: CSSProperties = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 };
const sep = <div role="separator" style={{ height: 1, background: "var(--hair)" }} />;
const overlaySt = (z: number): CSSProperties => ({ position: "fixed", inset: 0, zIndex: z, background: "rgba(10, 25, 49, 0.4)", display: "flex", justifyContent: "center", alignItems: "flex-start", padding: "48px 16px", boxSizing: "border-box", overflowY: "auto" });
const subDialogSt: CSSProperties = { background: "#fff", border: "1px solid var(--hair)", borderRadius: 8, boxShadow: "0 24px 64px rgba(15, 23, 42, 0.24)", boxSizing: "border-box", fontFamily: "var(--font-sans)", color: "var(--ink-700)", letterSpacing: "-0.02em" };
const ghostBtnSt: CSSProperties = { height: 48, borderRadius: 8, border: "1px solid var(--ink-200)", background: "#fff", color: "var(--ink-900)", fontFamily: "inherit", fontSize: 15, fontWeight: 500, letterSpacing: "-0.02em", cursor: "pointer" };
const blueBtnSt: CSSProperties = { height: 48, borderRadius: 8, border: "1px solid " + ACC, background: ACC, color: "#fff", fontFamily: "inherit", fontSize: 15, fontWeight: 500, letterSpacing: "-0.02em", cursor: "pointer" };

// 디자인 helmet 스타일 (hover · focus · placeholder · 인쇄) — 모달 안에서만 적용되도록 .wpl-root 로 범위 제한
const SCOPED_CSS = `
.wpl-root, .wpl-root * { font-family: ${FONT}; }
.wpl-root input::placeholder { color: var(--ink-300); }
.wpl-root input:focus, .wpl-root button[aria-haspopup]:focus-visible { outline: none; border-color: #1976D2 !important; box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.18); }
.wpl-root button:focus-visible { outline: 2px solid rgba(25, 118, 210, 0.45); outline-offset: 2px; }
.wpl-root .wpl-hb:hover { border-color: var(--ink-900) !important; }
.wpl-root .wpl-hs:hover { background: var(--bg-soft) !important; }
.wpl-root .wpl-hcal:hover { box-shadow: inset 0 0 0 1px var(--ink-300); }
.wpl-root .wpl-hblue:hover { background: #1565C0 !important; }
.wpl-root .wpl-hx:hover { background: var(--ink-300) !important; }
.wpl-root .wpl-hadd:hover { color: #0A1931 !important; }
.wpl-root .wpl-part:focus { border-bottom-color: var(--ink-900) !important; box-shadow: none; }
@media print {
  body * { visibility: hidden !important; }
  [data-print-root], [data-print-root] * { visibility: visible !important; }
  [data-print-root] { position: absolute !important; left: 0; top: 0; width: 100% !important; max-width: none !important; border: none !important; box-shadow: none !important; }
  [data-print-hide] { display: none !important; }
}
`;

interface DdOption { value: string; label: string; desc?: string; }
interface DdView {
    key: string; open: boolean; label: string; color: string; border: string; chevRot: string;
    onToggle: () => void; options: DdOption[]; value: string; onPick: (v: string) => void; minWidth?: number;
}

//variant "enrolled" (10/11 재학생 윈터 결제링크 발송하기): 이름·성별·전송번호 대신 재원생 선택, 결제 만료시간(H) 대신 결제선생 기한(d, 기본 7),
//  보내기 = 결제현황처럼 결제선생 바로 전송 (POST createwinterenrolledpaylink, 번호 = 결제선생 전송번호 > 학부모 > 학생). 2명 이상이면 자리 선택 비활성
const WinterPayLinkModal = (props: { open: boolean; handleClose: () => void; setUpdate: (v: number) => void; notify1?: () => void; user?: any; variant?: "staff" | "enrolled" }) => {

    const enrolled = props.variant === "enrolled";
    const TITLE = enrolled ? "재학생 윈터 결제링크 발송하기" : "윈터스쿨 결제링크 전송하기";
    // 재학생 발송 결과(제외·실패가 있으면 닫지 않고 보여줌)
    const [sendResult, setSendResult] = useState<{ sent: any[]; skipped: any[]; failed: any[] } | null>(null);

    const [cfg, setCfg] = useState<StaffConfig | null>(null);
    const [loadErr, setLoadErr] = useState<string>("");
    const [s, setS] = useState<S>(initialState);
    const [sending, setSending] = useState(false);
    // 이 화면에서 보낸 번호 기록 (중복 발송 경고용) — 모달을 닫아도 유지
    const [sentLog, setSentLog] = useState<{ phone: string; at: number }[]>([]);

    const sRef = useRef(s); sRef.current = s;
    const sendingRef = useRef(sending); sendingRef.current = sending;
    const rootRef = useRef<HTMLDivElement | null>(null);
    const calRef = useRef<HTMLDivElement | null>(null);
    const seatBuf = useRef("");
    const seatTimer = useRef<any>(null);

    // 클래스 setState 처럼 일부만 합치는 갱신 (null 이면 변경 없음)
    const set = useCallback((patch: Partial<S> | ((p: S) => Partial<S> | null)) => {
        setS(p => {
            const r = typeof patch === "function" ? patch(p) : patch;
            return r ? { ...p, ...r } : p;
        });
    }, []);

    // ===== 열 때 설정 불러오기 · 닫으면 모든 입력 초기화 =====
    useEffect(() => {
        if (!props.open) {
            setS(initialState());
            setCfg(null);
            setLoadErr("");
            setSending(false);
            setSendResult(null);
            return;
        }
        let alive = true;
        (async () => {
            try {
                const result: any = await getFetch("/dashboard/payments/winterpay/staffconfig");
                if (!alive) return;
                if (result && result.message === "success" && result.rates && Array.isArray(result.programs)) {
                    const c: StaffConfig = {
                        rates: result.rates, programs: result.programs, branches: result.branches || [],
                        seatRandomPid: result.seatRandomPid || "p3",
                        today: isYmd(result.today) ? result.today : ymd(new Date()),
                    };
                    setCfg(c);
                    // 처음 열 때 기본 시작일 = 가장 이른 윈터 시작일 (디자인 applyDefaultStart)
                    const t = toDate(c.today);
                    const first = presetSources(c, dayNum(t))[0];
                    const d = first ? fromDayNum(first.n) : null;
                    setS(p => ({ ...p, start: p.start || d, viewY: (p.start || d || t).getFullYear(), viewM: (p.start || d || t).getMonth() }));
                } else {
                    setLoadErr("설정을 불러오지 못했어요");
                    toast.error("윈터 금액 설정을 불러오지 못했어요");
                }
            } catch (e) {
                if (!alive) return;
                setLoadErr("설정을 불러오지 못했어요");
                toast.error("윈터 금액 설정을 불러오지 못했어요");
            }
        })();
        return () => { alive = false; };
    }, [props.open]);

    // 선택된 항목이 목록 가운데 오도록 스크롤 (목록이 그려진 뒤 실행)
    const scrollSelected = useCallback(() => {
        const run = () => {
            const root = rootRef.current;
            const el = root && root.querySelector('[role=listbox] [aria-selected="true"]') as HTMLElement | null;
            const list = el && el.closest("[role=listbox]") as HTMLElement | null;
            if (el && list) list.scrollTop = el.offsetTop - list.clientHeight / 2 + el.offsetHeight / 2;
        };
        requestAnimationFrame(() => requestAnimationFrame(run));
        setTimeout(run, 60);
    }, []);

    // ===== 문서 이벤트: 바깥 클릭 시 드롭다운 닫기 · Esc · 자리 숫자키 이동 =====
    useEffect(() => {
        if (!props.open) return;
        const onDown = (e: MouseEvent) => {
            const t = e.target as HTMLElement | null;
            if (sRef.current.openDd && !(t && t.closest && t.closest("[data-dd]"))) set({ openDd: null });
        };
        const onKey = (e: KeyboardEvent) => {
            const cur = sRef.current;
            if (e.key === "Escape") {
                // 드롭다운 → 확인창 → 명세서 → 모달 순서로 닫음 (학생 고르기 팝업은 자기 Esc 로 닫힘)
                if (cur.pickerOpen) return;
                if (cur.openDd) { set({ openDd: null }); return; }
                if (cur.confirmOpen) { if (!sendingRef.current) set({ confirmOpen: false }); return; }
                if (cur.stmtOpen) { set({ stmtOpen: false }); return; }
                if (!sendingRef.current) props.handleClose();
                return;
            }
            if (cur.openDd !== "seat") return;
            // 자리 선택: 숫자 키를 치면 해당 번호로 바로 이동 (0.8초 안에 이어 치면 여러 자리 숫자)
            if (/^[0-9]$/.test(e.key)) {
                e.preventDefault();
                clearTimeout(seatTimer.current);
                const buf = seatBuf.current + e.key;
                const n = Number(buf), one = Number(e.key);
                if (n >= 1 && n <= SEAT_MAX) { seatBuf.current = buf; set({ seat: String(n) }); scrollSelected(); }
                else { seatBuf.current = e.key; if (one >= 1) { set({ seat: String(one) }); scrollSelected(); } }
                seatTimer.current = setTimeout(() => { seatBuf.current = ""; }, 800);
            } else if (e.key === "Enter") {
                e.preventDefault();
                set({ openDd: null });
            }
        };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
            clearTimeout(seatTimer.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.open]);

    // 달력 넘김 모션 (동작 줄이기 설정 시 생략)
    const animateCal = (n: number) => {
        const el = calRef.current;
        if (!el || !(el as any).animate) return;
        if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        el.querySelectorAll("[data-cal-anim]").forEach((x: any) => {
            if (x.getAnimations) x.getAnimations().forEach((a: any) => a.cancel());
            x.animate(
                [{ transform: "translateX(" + (n > 0 ? 32 : -32) + "px)", opacity: 0 }, { transform: "translateX(0)", opacity: 1 }],
                { duration: 260, easing: "cubic-bezier(0.2, 0.7, 0.2, 1)" }
            );
        });
    };
    const shift = (n: number) => {
        set(p => { const d = new Date(p.viewY, p.viewM + n, 1); return { viewY: d.getFullYear(), viewM: d.getMonth() }; });
        requestAnimationFrame(() => animateCal(n));
    };

    // Joy Modal: 바깥 클릭으로는 닫지 않음(입력 보호), Esc 는 위 문서 이벤트에서 처리
    const onModalClose = (_e: any, reason: string) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        if (!sending) props.handleClose();
    };

    // ===== 로딩 화면 =====
    const shell = (body: React.ReactNode) => (
        <Modal open={props.open} onClose={onModalClose} disableEscapeKeyDown>
            <div ref={rootRef} className="wpl-root" style={{ ...ROOT_VARS, position: "fixed", inset: 0, display: "flex", justifyContent: "center", alignItems: "flex-start", padding: "60px 16px", boxSizing: "border-box", overflow: "hidden", fontFamily: "var(--font-sans)", color: "var(--ink-700)", letterSpacing: "-0.02em", outline: "none" }}>
                <style>{SCOPED_CSS}</style>
                {body}
            </div>
        </Modal>
    );

    if (!props.open) return null;

    const closeBtn = (
        <button type="button" onClick={() => { if (!sending) props.handleClose(); }} title="닫기" aria-label="닫기" className="wpl-hs" style={{ width: 40, height: 40, flex: "none", borderRadius: "50%", border: "1px solid var(--hair)", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--ink-900)" strokeWidth="1.5" strokeLinecap="round"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
        </button>
    );

    if (!cfg) {
        return shell(
            <div role="dialog" style={{ width: "100%", maxWidth: 650, background: "#fff", border: "1px solid var(--hair)", borderRadius: 8, boxSizing: "border-box", display: "flex", flexDirection: "column", boxShadow: "0 2px 8px -2px rgba(21, 21, 21, 0.08), 0 6px 12px -2px rgba(21, 21, 21, 0.08)" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "28px 32px 20px", borderBottom: "1px solid var(--hair)" }}>
                    <h2 style={{ margin: 0, fontSize: 28, fontWeight: 300, lineHeight: 1.3, letterSpacing: "-0.025em", color: "var(--ink-900)" }}>{TITLE}</h2>
                    {closeBtn}
                </div>
                <div style={{ padding: "64px 32px", textAlign: "center", fontSize: 15, color: loadErr ? INVALID : "var(--ink-500)" }}>
                    {loadErr || "금액 설정을 불러오는 중이에요..."}
                </div>
            </div>
        );
    }

    // ===== 계산 (디자인 renderVals) =====
    const todayN = dayNum(toDate(cfg.today));
    const rates = cfg.rates;
    const branchKo = (en: string) => (cfg.branches.find(b => b.english === en) || { korean: en }).korean;
    const start = s.start, end = s.end;

    // 혜택 목록: 자동 적용 혜택은 조건이 맞으면 강제로 켜짐(직접 해제 불가), 제외 지점이면 꺼짐
    const benefitList = (st: S) => rates.benefits.map(c => {
        const met = benefitMet(c, cfg.today);
        const excluded = !!st.branch && (c.excl || []).includes(st.branch);
        const locked = !excluded && met && c.auto !== false;
        return { c, met, excluded, locked, on: !excluded && (locked || !!st.checks[c.id]) };
    });
    const inputOf = (st: S) => ({
        rates, type: (st.type || "general") as "general" | "student", branch: st.branch, start: st.start, end: st.end,
        extra: 0, activeIds: benefitList(st).filter(x => x.on).map(x => x.c.id),
    });
    const priceOf = (st: S): number | null => {
        const manualN = Number(digits(st.manual)) || 0;
        if (!manualN && !st.type) return null; // 학생 구분 전엔 계산 안 함
        return calcRate({ ...inputOf(st), manualPrice: manualN });
    };
    const groups = (segs: ReturnType<typeof rateSegments>) => {
        const m = new Map<string, { name: string; days: number; maxDisc: number; monthly: Set<number> }>();
        segs.forEach(g => {
            const o = m.get(g.name) || { name: g.name, days: 0, maxDisc: 0, monthly: new Set<number>() };
            o.days += segDays(g); o.maxDisc = Math.max(o.maxDisc, g.discount); o.monthly.add(g.monthly);
            m.set(g.name, o);
        });
        return Array.from(m.values());
    };
    // 명세서
    const statementOf = (st: S) => {
        if (!st.type) return null;
        const inp = inputOf(st);
        const auto = calcRate(inp);
        const segs = rateSegments(inp);
        if (auto == null || !segs.length) return null;
        const row = rates.prices[inp.branch];
        const multi = new Set(segs.map(g => g.name)).size > 1 || segs[0].name !== "기본";
        const rows: any[] = [];
        segs.forEach(g => prorateBreakdown(g.monthly, g.start, g.end, g.discount).forEach(r => rows.push({ ...r, name: g.name, discount: g.discount })));
        const sum = rows.reduce((a, r) => a + r.amount, 0);
        if (sum !== auto) console.warn("명세서 내역 합계 불일치", sum, auto);
        const manual = Number(digits(st.manual)) || 0;
        const basis = [{ label: "학생 구분", value: (TYPES.find(t => t.key === st.type) || TYPES[0]).label }, { label: "기본 월 금액", value: won(row.base) }];
        groups(segs).forEach(g => {
            if (g.name !== "기본") Array.from(g.monthly).filter(v => v !== row.base).forEach(v => basis.push({ label: g.name + " 월 금액", value: won(v) }));
        });
        const usedIds = new Set<string>();
        segs.forEach(g => g.bIds.forEach(id => usedIds.add(id)));
        const used = rates.benefits.filter(c => usedIds.has(c.id));
        if (used.length) basis.push({ label: "등록 혜택", value: used.map(c => c.name + " " + c.pct + "%").join(" · ") });
        const md = (d: Date) => (d.getMonth() + 1) + "." + d.getDate();
        const totals = manual
            ? [
                { label: "자동 계산 금액", value: won(auto), weight: 400, size: "15px", color: "var(--ink-500)", strike: "line-through", manual: false },
                { label: "최종 금액", value: won(manual), weight: 500, size: "24px", color: "var(--ink-900)", strike: "none", manual: true },
            ]
            : [{ label: "최종 금액", value: won(auto), weight: 500, size: "24px", color: "var(--ink-900)", strike: "none", manual: false }];
        let note = "한 달 전체를 등록한 달은 월 금액을, 일부만 등록한 달은 월 금액 ÷ 그 달 일수 × 등록일수로 계산하며 10원 미만은 절사합니다.";
        if (multi) note += " 기간마다 정해진 금액과 할인율을 적용하고, 한 달 안에서 바뀌면 구간별로 나눠 계산합니다.";
        if (used.length) note += " 등록 혜택은 결제일(" + mdKo(cfg.today) + ") 기준입니다.";
        if (used.length > 1) note += " 같은 날 할인이 겹치면 가장 큰 할인 하나만 적용합니다.";
        if (segs.some(g => g.discount)) note += " 할인은 월 금액에 먼저 적용한 뒤 10원 미만을 절사합니다.";
        if (manual) note += " 최종 금액은 상담을 통해 별도로 정한 금액입니다.";
        return {
            basis, totals, note, segs, used,
            rows: rows.map(r => ({
                month: r.y + "년 " + (r.m + 1) + "월" + (multi ? " · " + r.name : "") + (r.discount ? " · " + r.discount + "% 할인" : ""),
                range: md(r.s) + " ~ " + md(r.e),
                days: r.F === r.w ? "한 달 전체 · " + r.w + "일" : r.F + "일 / " + r.w + "일",
                amount: won(r.amount),
            })),
        };
    };

    const isManual = !!(Number(digits(s.manual)) || 0);
    // 지점·기간·학생 구분 미선택이면 (수동 금액이 없을 때) "-"
    const price = isManual || (s.branch && s.type && start && end) ? priceOf(s) : null;

    // 분할 결제: 회차 수만큼 균등 분할(10원 단위, 차액은 마지막 회차). 앞 회차를 고치면 마지막 회차 = 남은 금액
    const splitN = s.split ? Math.max(2, s.splitN || 2) : 0;
    const eqBase = price != null && splitN ? Math.floor(price / splitN / 10) * 10 : 0;
    const heads = s.split ? Array.from({ length: splitN - 1 }, (_, i) => s.splitAuto !== false ? String(eqBase) : String((s.parts || [])[i] ?? "")) : [];
    const headNums = heads.map(v => Number(digits(v)) || 0);
    const lastNum = price != null ? price - headNums.reduce((a, b) => a + b, 0) : 0;
    const parts = s.split ? [...heads, String(Math.max(lastNum, 0))] : [];
    const partNums = s.split ? [...headNums, lastNum] : [];
    const splitOk = !s.split || (price != null && partNums.every(n => n > 0));
    const splitErr = lastNum <= 0 ? "마지막 회차가 0원 이하예요. 앞 회차를 줄여주세요" : "0원인 회차가 있어요. 회차를 줄여주세요";
    const splitTxt = s.split ? splitN + "회 · " + partNums.map((n, i) => (i + 1) + "회차 " + comma(n) + "원").join(" · ") + " · 알림톡 " + splitN + "통" : "";

    const phoneOk = /^010\d{8}$/.test(digits(s.phone));
    const hoursN = Number(s.hours) || 0;
    const hoursOk = Number.isInteger(hoursN) && hoursN >= 1 && hoursN <= 720;
    // 재학생 모드: 결제선생 기한(일) — 결제선생은 날짜 단위 만료
    const daysN = Number(s.days) || 0;
    const daysOk = Number.isInteger(daysN) && daysN >= 1 && daysN <= 60;
    const expAt = enrolled ? (() => { const d = new Date(); d.setDate(d.getDate() + daysN); d.setHours(23, 59, 0, 0); return d; })() : new Date(Date.now() + hoursN * 3600e3);
    const expTxt = enrolled
        ? (expAt.getMonth() + 1) + "/" + expAt.getDate() + "(" + WK[expAt.getDay()] + ")까지"
        : (expAt.getMonth() + 1) + "/" + expAt.getDate() + "(" + WK[expAt.getDay()] + ") " + pad2(expAt.getHours()) + ":" + pad2(expAt.getMinutes());
    const studentN = s.students.length;
    const missing = ([
        ...(enrolled ? [
            [studentN === 0, "학생을 선택해주세요"],
        ] : [
            [!s.name.trim(), "이름을 입력해주세요"],
            [!s.gender, "학생 성별을 선택해주세요"],
            [!phoneOk, "전송 전화번호를 확인해주세요"],
        ]),
        [!s.branch, "지점을 선택해주세요"],
        [!start || !end, "등록 기간을 선택해주세요"],
        [!s.type, "학생 구분을 선택해주세요"],
        enrolled ? [!daysOk, "결제선생 기한을 1~60일로 입력해주세요"] : [!hoursOk, "결제 만료 시간을 1~720시간으로 입력해주세요"],
        [!!s.editing && !isManual, "결제 금액을 입력해주세요"],
        [!splitOk, splitErr],
        [!!start && !!end && !winterOk(cfg, s.branch, start, dayNum(end), "end"), "윈터 종료일은 정해진 날짜만 가능해요"],
        [price === 0, "결제 금액이 0원이라 보낼 수 없어요"],
        [price == null, "금액을 계산할 수 없어요"],
    ] as [boolean, string][]).find(x => x[0]);
    const ready = !missing && !sending;

    // 시작일이 정규윈터(seatRandomPid) 기간 안이면 자리 잠금 — 자리 값은 보내지 않음
    const seatP = rates.periods.find(p => p.id === cfg.seatRandomPid);
    const regularOnly = !!start && !!seatP && rangesOf(seatP.ranges).some(([a, z]) => dayNum(start) >= a && dayNum(start) <= z);
    const pastStart = !!start && dayNum(start) < todayN;
    const stmt = statementOf(s);
    const blist = benefitList(s);

    // '○○부터' 버튼: 시작일만 채움 → 종료일은 드롭다운. 기존 종료일이 새 시작일에 맞으면 유지
    const mdS = (d: Date) => pad2(d.getMonth() + 1) + "." + pad2(d.getDate());
    const presets = presetSources(cfg, todayN).map(x => {
        const sN = start ? dayNum(start) : null, eN = end ? dayNum(end) : sN;
        const a = sN != null && eN != null && sN <= x.z && eN >= x.a; // 선택한 등록 기간과 겹치면 활성
        const sd0 = fromDayNum(x.n), ed = fromDayNum(x.z);
        return {
            key: x.name + x.a, label: x.name + "부터", sub: mdS(sd0) + " ~ " + mdS(ed), active: a,
            onClick: () => set(p => {
                // 고정 시작 지점(강남·대치 정규윈터 등)은 그 프로그램의 고정일로 맞춤 — 디자인엔 없던 보정
                const prog = cfg.programs.find(q => inProg(cfg, q, x.n));
                const sd = prog && prog.fixedStart && prog.fixedStart.branches.includes(p.branch) && isYmd(prog.fixedStart.date) ? toDate(prog.fixedStart.date) : sd0;
                const n = dayNum(sd);
                const keep = p.end && winterOk(cfg, p.branch, sd, dayNum(p.end), "end", n);
                return { start: sd, end: keep ? p.end : null, openDd: null, ...RESET_COST, viewY: sd.getFullYear(), viewM: sd.getMonth() };
            }),
        };
    });

    // 달력 클릭 = 시작일 변경 (종료일은 드롭다운). 새 시작일에 맞으면 종료일 유지
    const pick = (d: Date) => set(p => {
        const n = dayNum(d);
        if (d.getDay() === 0 || !winterOk(cfg, p.branch, p.start, n, "start")) return null;
        const keep = p.end && winterZone(cfg, n) && winterOk(cfg, p.branch, p.start, dayNum(p.end), "end", n);
        return { start: d, end: keep ? p.end : null, openDd: null, ...RESET_COST };
    });

    const buildMonth = (y: number, m: number, idx: number) => {
        const first = new Date(y, m, 1).getDay();
        const days = daysIn(y, m);
        const same = (a: Date | null, b: Date | null) => !!a && !!b && dayNum(a) === dayNum(b);
        const cells: any[] = [];
        const empty = { day: "", off: true, vis: "hidden", band: "transparent", radius: "0", dot: "transparent", border: "transparent", color: "transparent", weight: 400, onClick: null };
        for (let i = 0; i < first; i++) cells.push(empty);
        for (let d = 1; d <= days; d++) {
            const dt = new Date(y, m, d), n = dayNum(dt);
            const isEnd = same(dt, start) || same(dt, end);
            const inRange = !!start && !!end && n > dayNum(start) && n < dayNum(end);
            const c = (first + d - 1) % 7;
            const multi = !!start && !!end && !same(start, end);
            const off = c === 0 || !winterOk(cfg, s.branch, start, n, "start"); // 윈터: 정해진 시작일 외 비활성
            const inBand = multi && (inRange || isEnd);
            const L = same(dt, start) || c === 0 || d === 1;
            const R = same(dt, end) || c === 6 || d === days;
            const r = (on: boolean) => (on ? "20px" : "0");
            cells.push({
                day: d, off, vis: "visible",
                band: inBand ? CAL.band : "transparent",
                radius: inBand ? [r(L), r(R), r(R), r(L)].join(" ") : "0",
                dot: isEnd ? CAL.accent : "transparent",
                border: isEnd ? CAL.accent : n === todayN ? "var(--ink-700)" : "transparent",
                color: isEnd ? "#fff" : off ? (inBand ? "var(--ink-500)" : "var(--ink-300)") : c === 0 ? "var(--ink-400)" : "var(--ink-900)",
                weight: isEnd ? 500 : 400,
                onClick: off ? null : () => pick(dt),
            });
        }
        while (cells.length < 42) cells.push(empty); // 항상 6주 고정
        return { key: y + "-" + m, title: y + "년 " + (m + 1) + "월", cells, divider: idx === 0 ? "transparent" : "var(--hair)", prevVis: idx === 0 ? "visible" : "hidden", nextVis: idx === 1 ? "visible" : "hidden" };
    };
    const next = new Date(s.viewY, s.viewM + 1, 1);
    const months = [buildMonth(s.viewY, s.viewM, 0), buildMonth(next.getFullYear(), next.getMonth(), 1)];

    // 커스텀 드롭다운: 칸과 같은 모양의 목록을 띄움
    const dd = (key: string, value: string, opts: DdOption[], onPick: (v: string) => void, placeholder: string, minWidth?: number): DdView => {
        const open = s.openDd === key;
        const cur = opts.find(o => o.value === value);
        return {
            key, open, value, onPick, minWidth, options: opts,
            label: cur ? cur.label : placeholder,
            color: cur ? "var(--ink-900)" : "var(--ink-300)",
            border: open ? ACC : "var(--ink-200)",
            chevRot: open ? "rotate(180deg)" : "none",
            onToggle: () => { set(p => ({ openDd: p.openDd === key ? null : key })); if (!open) scrollSelected(); },
        };
    };

    // 지점: 시작일이 정해진 지점(강남·대치 계열 정규윈터)으로 바꾸면 시작일을 그 날짜로 자동 맞춤
    const branchDd = dd("branch", s.branch, cfg.branches.map(b => ({ value: b.english, label: b.korean })), v => set(p => {
        const n = p.start ? dayNum(p.start) : null;
        const prog = n != null ? cfg.programs.find(x => inProg(cfg, x, n)) : null;
        const fx = prog && prog.fixedStart && prog.fixedStart.branches.includes(v) && isYmd(prog.fixedStart.date) ? toDate(prog.fixedStart.date) : null;
        const fixStart = fx && dayNum(fx) !== n
            ? { start: fx, end: p.end && winterOk(cfg, v, fx, dayNum(p.end), "end", dayNum(fx)) ? p.end : null, viewY: fx.getFullYear(), viewM: fx.getMonth() }
            : {};
        return { branch: v, ...RESET_COST, ...fixStart };
    }), "지점을 선택해주세요", 300);

    const seatOpts: DdOption[] = [{ value: "", label: "선택 안 함" }].concat(Array.from({ length: SEAT_MAX }, (_, i) => ({ value: String(i + 1), label: (i + 1) + "번 자리" })));
    // 재학생 모드 2명 이상이면 자리 선택 안 함
    const seatLockedMulti = enrolled && studentN >= 2;
    const seatDd: DdView = seatLockedMulti
        ? { ...dd("seat", "", [], () => { }, ""), open: false, label: "2명 이상은 자리 선택 안 함", color: "var(--ink-300)", border: "var(--ink-200)", onToggle: () => { } }
        : regularOnly
        ? { ...dd("seat", "", [], () => { }, ""), open: false, label: "정규윈터는 12월 중순 임의 배정", color: "var(--ink-300)", border: "var(--ink-200)", onToggle: () => { } }
        : dd("seat", s.seat, seatOpts, v => set({ seat: v }), "선택 안 함", 300);

    // 종료일 드롭다운 — 정해진 종료일 중 선택, 일수·금액 함께 표시
    const endOpts: DdOption[] = start ? winterEnds(cfg, s.branch, dayNum(start)).map(toDate).filter(e => dayNum(e) >= dayNum(start)).map(e => {
        const pr = s.branch && s.type ? priceOf({ ...s, end: e, manual: "", editing: false, splitAuto: true, parts: [] }) : null;
        return {
            value: ymd(e), label: e.getFullYear() + "년 " + pad2(e.getMonth() + 1) + "월 " + pad2(e.getDate()) + "일(" + WK[e.getDay()] + ")",
            desc: "총 " + (dayNum(e) - dayNum(start) + 1) + "일" + (pr != null ? " · " + comma(pr) + "원" : ""),
        };
    }) : [];
    const endDd = dd("wEnd", end ? ymd(end) : "", endOpts, v => set({ end: toDate(v), ...RESET_COST }), "종료일을 선택해주세요");

    // 혜택 마감 경고: 결제 만료 시각이 혜택 마감을 넘으면
    const warnings = blist
        .filter(x => x.on && x.met && x.c.type !== "always")
        .filter(x => dayNum(expAt) > (x.c.type === "until" ? dayNum(toDate(x.c.dates[0])) : todayN))
        .map(x => "결제 만료 시각(" + (expAt.getMonth() + 1) + "/" + expAt.getDate() + " " + pad2(expAt.getHours()) + ":" + pad2(expAt.getMinutes()) + ')이 "' + x.c.name + '" 마감을 넘습니다. 마감 전에 결제해야 혜택이 유지됩니다.');

    const tip = (key: string) => ({
        tipOn: s.tipKey === key,
        tipEnter: () => set({ tipKey: key }),
        tipLeave: () => set(p => (p.tipKey === key ? { tipKey: null } : null)),
    });
    const benefits = blist.map(({ c, met, on, excluded, locked }) => {
        if (excluded) return {
            id: c.id, badge: "제외 지점", badgeBg: "var(--bg-soft)", badgeColor: "var(--ink-500)", badgeBorder: "var(--bg-soft)",
            ...tip("bf-" + c.id),
            label: c.name + " · " + c.pct + "%", desc: branchKo(s.branch) + "은 이 혜택 대상이 아니에요", on: false,
            fill: "var(--bg-soft)", boxBorder: "var(--ink-200)", cursor: "default", disabled: true, onClick: () => { },
        };
        const where = periodsText(c.periodIds, rates.periods) + " 날짜에 적용";
        const desc = c.type === "always"
            ? where
            : condText(c) + " · " + (locked ? "설정에서 자동 적용 · " + where : on ? (met ? "" : "직접 적용 · ") + where : "적용 안 함");
        const badge = c.type === "always"
            ? { badge: "항상", badgeBg: "#fff", badgeColor: "var(--ink-700)", badgeBorder: "var(--ink-300)" }
            : met
                ? { badge: "오늘 해당", badgeBg: ACC_SOFT, badgeColor: ACC, badgeBorder: ACC_SOFT }
                : { badge: "해당 안 됨", badgeBg: "var(--bg-soft)", badgeColor: "var(--ink-500)", badgeBorder: "var(--bg-soft)" };
        return {
            id: c.id, ...badge, ...tip("bf-" + c.id),
            label: c.name + " · " + c.pct + "%", desc, on,
            fill: locked ? "var(--ink-300)" : on ? ACC : "#fff", boxBorder: locked ? "var(--ink-300)" : on ? ACC : "var(--ink-300)",
            cursor: locked ? "default" : "pointer", disabled: locked,
            onClick: () => { if (locked) return; set(p => ({ checks: { ...p.checks, [c.id]: !on } })); },
        };
    });

    // 하단 실시간 금액 바
    const mdW = (d: Date) => (d.getMonth() + 1) + "." + d.getDate() + "(" + WK[d.getDay()] + ")";
    const barPeriod = start && end ? mdW(start) + " ~ " + mdW(end) + " · " + (dayNum(end) - dayNum(start) + 1) + "일"
        : start ? mdW(start) + " ~ 종료일을 선택해주세요" : "달력에서 시작일을 선택해주세요";

    // 발송 전 확인
    const md3 = (d: Date) => d.getFullYear() + "." + (d.getMonth() + 1) + "." + d.getDate() + "(" + WK[d.getDay()] + ")";
    const dup = sentLog.filter(x => x.phone === digits(s.phone)).slice(-1)[0];
    const benefitTxt = stmt ? ((stmt.basis.find(x => x.label === "등록 혜택") || { value: "" }).value) : "";
    const confirmRows: [string, string][] = [
        ...(enrolled
            ? [["학생", studentN + "명 · " + s.students.slice(0, 5).map(studentLabel).join(", ") + (studentN > 5 ? " 외 " + (studentN - 5) + "명" : "")]] as [string, string][]
            : [["이름", s.name.trim()], ["성별", s.gender === "female" ? "여학생" : "남학생"], ["전송 번호", fmtTel(s.phone)]] as [string, string][]),
        ["지점", branchKo(s.branch)], ["자리", seatLockedMulti ? "2명 이상 · 선택 안 함" : regularOnly ? "정규윈터 · 임의 배정" : s.seat ? s.seat + "번" : "선택 안 함"],
        ["학생 구분", s.type ? (TYPES.find(t => t.key === s.type) || TYPES[0]).label : "-"],
        ["등록 기간", start && end ? md3(start) + " ~ " + md3(end) + " · " + (dayNum(end) - dayNum(start) + 1) + "일" : "-"],
        ["할인", [benefitTxt, isManual ? "수동 금액" : ""].filter(Boolean).join(" · ") || "없음"],
        ...(s.split ? [["분할 결제", enrolled ? splitTxt.replace(/알림톡 (\d+)통/, "결제선생 $1건씩") : splitTxt] as [string, string]] : []),
        enrolled ? ["결제선생 기한", daysN + "일 · " + expTxt] : ["링크 마감", expTxt + " (지금 보낼 때)"],
    ];

    // ===== 발송 =====
    const doSend = async () => {
        if (enrolled) { await doSendEnrolled(); return; }
        if (!ready || !start || !end || price == null || !s.type || !s.gender) return;
        setSending(true);
        try {
            const inp = inputOf(s);
            const segs = rateSegments(inp);
            const usedIds = new Set<string>();
            segs.forEach(g => g.bIds.forEach(id => usedIds.add(id)));
            const body = {
                location: s.branch,
                seat: regularOnly ? null : (s.seat ? Number(s.seat) : null),
                startDateTime: new Date(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0, 0).getTime(),
                endDateTime: new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999).getTime(),
                price,
                parts: s.split ? partNums : null,
                phoneNumber: digits(s.phone),
                name: s.name.trim(),
                dueHours: hoursN,
                gender: s.gender,
                studentType: s.type,
                priceDetail: {
                    segments: segs.map(g => ({ name: g.name, pid: g.pid, monthly: g.monthly, discount: g.discount, start: ymd(g.start), end: ymd(g.end) })),
                    activeIds: inp.activeIds,
                    manual: isManual,
                    benefits: rates.benefits.filter(c => usedIds.has(c.id)).map(c => c.name),
                },
            };
            const result: any = await postFetch("/dashboard/payments/managepayments/createwinterpaylink", body);
            if (result && result.message === "success") {
                setSentLog(l => [...l, { phone: digits(s.phone), at: Date.now() }]);
                if (props.notify1) props.notify1();
                props.setUpdate(Math.random());
                setSending(false);
                props.handleClose();
                toast.success("윈터 결제링크를 보냈어요");
            } else {
                setSending(false);
                toast.error((result && result.message) || "링크를 보내지 못했어요");
            }
        } catch (e) {
            setSending(false);
            toast.error("링크를 보내는 중 오류가 발생했어요");
        }
    };

    // 재학생: 결제선생 바로 전송 (결제현황과 같은 방식)
    const doSendEnrolled = async () => {
        if (!ready || !start || !end || price == null || !s.type || studentN === 0) return;
        setSending(true);
        try {
            const inp = inputOf(s);
            const segs = rateSegments(inp);
            const usedIds = new Set<string>();
            segs.forEach(g => g.bIds.forEach(id => usedIds.add(id)));
            const body = {
                userIds: s.students.map(x => x.id),
                location: s.branch,
                seat: seatLockedMulti || regularOnly ? null : (s.seat ? Number(s.seat) : null),
                startDateTime: new Date(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0, 0).getTime(),
                endDateTime: new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999).getTime(),
                price,
                parts: s.split ? partNums : null,
                dueDays: daysN,
                studentType: s.type,
                priceDetail: {
                    segments: segs.map(g => ({ name: g.name, pid: g.pid, monthly: g.monthly, discount: g.discount, start: ymd(g.start), end: ymd(g.end) })),
                    activeIds: inp.activeIds,
                    manual: isManual,
                    benefits: rates.benefits.filter(c => usedIds.has(c.id)).map(c => c.name),
                },
            };
            const result: any = await postFetch("/dashboard/payments/managepayments/createwinterenrolledpaylink", body);
            setSending(false);
            if (result && result.message === "success") {
                const sent = result.sent || [], skipped = result.skipped || [], failed = result.failed || [];
                props.setUpdate(Math.random());
                set({ confirmOpen: false });
                if (skipped.length === 0 && failed.length === 0) {
                    if (props.notify1) props.notify1();
                    props.handleClose();
                    toast.success(`결제선생을 ${sent.length}명에게 보냈어요`);
                } else {
                    setSendResult({ sent, skipped, failed });
                    toast.info(`보냄 ${sent.length}명 · 제외 ${skipped.length}명 · 실패 ${failed.length}명`);
                }
            } else {
                toast.error((result && result.message) || "결제선생을 보내지 못했어요");
            }
        } catch (e) {
            setSending(false);
            toast.error("결제선생을 보내는 중 오류가 발생했어요");
        }
    };

    const sendLabel = enrolled && !sending && !missing ? (s.split ? `결제선생 ${studentN}명 · 회차 ${splitN}건씩 발송` : `결제선생 ${studentN}명에게 발송`) : sending ? "보내는 중..." : missing ? missing[1] : s.split ? "링크 " + splitN + "개 한 번에 발송" : "링크 발송하기";

    // ===== 그리기 =====
    const chev = (rot: string) => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ink-500)" strokeWidth="1.5" style={{ position: "absolute", right: 16, top: 16, pointerEvents: "none", transform: rot, transition: "transform 0.15s ease" }}><path d="m6 9 6 6 6-6"></path></svg>
    );
    const renderDd = (v: DdView) => (
        <div data-dd="" style={{ position: "relative" }}>
            <button type="button" onClick={v.onToggle} aria-haspopup="listbox" aria-expanded={v.open} className="wpl-hb" style={{ height: 48, width: "100%", minWidth: 0, padding: "0 44px 0 16px", border: "1px solid " + v.border, borderRadius: 8, background: "#fff", fontFamily: "inherit", fontSize: 15, color: v.color, letterSpacing: "-0.02em", textAlign: "left", cursor: "pointer", display: "flex", alignItems: "center", boxSizing: "border-box", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{v.label}</button>
            {chev(v.chevRot)}
            {v.open &&
                <div role="listbox" style={{ position: "absolute", left: 0, right: 0, minWidth: v.minWidth, top: "calc(100% + 4px)", zIndex: 30, maxHeight: 288, overflowY: "auto", background: "#fff", border: "1px solid var(--ink-200)", borderRadius: 8, padding: 4, boxShadow: "0 12px 32px rgba(15, 23, 42, 0.14)", boxSizing: "border-box", display: "flex", flexDirection: "column", gap: 2 }}>
                    {v.options.map(op => {
                        const sel = op.value === v.value;
                        return (
                            <button key={op.value} type="button" role="option" aria-selected={sel} className="wpl-hs" onClick={() => { set({ openDd: null }); v.onPick(op.value); }} style={{ width: "100%", minHeight: 44, flex: "none", padding: "0 12px", border: "none", borderRadius: 6, background: sel ? ACC_SOFT : "transparent", fontFamily: "inherit", fontSize: 15, fontWeight: sel ? 500 : 400, color: "var(--ink-900)", letterSpacing: "-0.02em", textAlign: "left", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                                <span style={{ display: "flex", flexDirection: "column", gap: 2, padding: "8px 0" }}>
                                    <span>{op.label}</span>
                                    {!!op.desc && <span style={{ fontSize: 13, fontWeight: 400, color: "var(--ink-500)" }}>{op.desc}</span>}
                                </span>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1976D2" strokeWidth="1.75" style={{ flex: "none", visibility: sel ? "visible" : "hidden" }}><path d="M20 6 9 17l-5-5"></path></svg>
                            </button>
                        );
                    })}
                </div>
            }
        </div>
    );

    return shell(
        <>
            <div role="dialog" data-screen-label="결제링크 전송하기" style={{ width: "100%", maxWidth: 650, height: "min(1078px, calc(100vh - 120px))", background: "#fff", border: "1px solid var(--hair)", borderRadius: 8, boxSizing: "border-box", padding: 0, overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 2px 8px -2px rgba(21, 21, 21, 0.08), 0 6px 12px -2px rgba(21, 21, 21, 0.08)" }}>

                {/* 헤더 */}
                <div style={{ flex: "none", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "28px 32px 20px", borderBottom: "1px solid var(--hair)", overflowY: "hidden", scrollbarGutter: "stable" } as CSSProperties}>
                    <h2 style={{ margin: 0, minWidth: 0, fontSize: 28, fontWeight: 300, lineHeight: 1.3, letterSpacing: "-0.025em", color: "var(--ink-900)" }}>{TITLE}</h2>
                    {closeBtn}
                </div>

                {/* 본문 (스크롤) */}
                <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain", padding: "28px 32px 32px", display: "flex", flexDirection: "column", gap: 28 }}>
                    {enrolled ? (
                        <div style={col(10)}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                                <span style={labelSt}>학생 선택*{studentN > 0 ? <span style={{ marginLeft: 6, fontWeight: 400, color: "var(--ink-500)" }}>{studentN}명</span> : null}</span>
                                <button type="button" onClick={() => set({ pickerOpen: true })} title="학생 고르기" aria-label="학생 고르기" className="wpl-hs" style={{ width: 36, height: 36, flex: "none", borderRadius: "50%", border: "1px solid var(--hair)", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ink-900)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"></path></svg>
                                </button>
                            </div>
                            {studentN === 0 ? (
                                <div onClick={() => set({ pickerOpen: true })} style={{ ...inputSt, display: "flex", alignItems: "center", color: "var(--ink-300)", cursor: "pointer" }}>-</div>
                            ) : (
                                <div style={col(6)}>
                                    <div style={{ fontSize: 15, lineHeight: 1.6, color: "var(--ink-900)", ...(s.moreOpen ? {} : { display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }) } as CSSProperties}>
                                        {s.students.map(studentLabel).join(", ")}
                                    </div>
                                    {(s.students.map(studentLabel).join(", ").length > 100 || s.moreOpen) &&
                                        <button type="button" onClick={() => set({ moreOpen: !s.moreOpen })} style={{ alignSelf: "flex-start", border: "none", background: "none", padding: 0, fontFamily: "inherit", fontSize: 14, color: ACC, cursor: "pointer" }}>{s.moreOpen ? "접기" : "더보기"}</button>
                                    }
                                </div>
                            )}
                        </div>
                    ) : (<>
                    <div style={col(10)}>
                        <label style={labelSt}>이름*</label>
                        <input value={s.name} onChange={e => set({ name: e.target.value })} placeholder="이름을 입력해주세요" style={inputSt} />
                    </div>

                    <div style={col(12)}>
                        <span style={labelSt}>학생 성별*</span>
                        <div role="radiogroup" style={grid2}>
                            {([["male", "남학생"], ["female", "여학생"]] as ["male" | "female", string][]).map(([v, label]) => (
                                <button key={v} type="button" role="radio" aria-checked={s.gender === v} className="wpl-hb" onClick={() => set({ gender: v })} style={segSt(s.gender === v)}>{label}</button>
                            ))}
                        </div>
                    </div>

                    <div style={col(10)}>
                        <label style={labelSt}>전송 전화번호*</label>
                        <input value={fmtTel(s.phone)} onChange={e => set({ phone: digits(e.target.value).slice(0, 11) })} inputMode="numeric" style={inputSt} />
                    </div>
                    </>)}

                    {sep}

                    <div style={col(10)}>
                        <label style={labelSt}>지점 선택*</label>
                        {renderDd(branchDd)}
                    </div>

                    <div style={col(10)}>
                        <label style={labelSt}>자리 선택</label>
                        {renderDd(seatDd)}
                    </div>

                    <div style={col(12)}>
                        <span style={labelSt}>학생 구분*</span>
                        <div role="radiogroup" style={grid2}>
                            {TYPES.map(t => (
                                <span key={t.key} style={col(8)}>
                                    <button type="button" role="radio" aria-checked={s.type === t.key} className="wpl-hb" onClick={() => set({ type: t.key, ...RESET_COST })} style={segSt(s.type === t.key)}>{t.label}</button>
                                </span>
                            ))}
                        </div>
                    </div>

                    <div style={col(10)}>
                        <span style={labelSt}>등록 날짜 설정*</span>
                        {presets.length > 0 &&
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8 }}>
                                {presets.map(pr => (
                                    <button key={pr.key} type="button" aria-pressed={pr.active} className="wpl-hb" onClick={pr.onClick} style={{ minHeight: 56, minWidth: 0, padding: "8px 12px", borderRadius: 8, border: "1px solid " + (pr.active ? ACC : "var(--ink-200)"), background: pr.active ? ACC_SOFT : "#fff", fontFamily: "inherit", letterSpacing: "-0.02em", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2 }}>
                                        <span style={{ fontSize: 15, fontWeight: 500, color: pr.active ? ACC : "var(--ink-900)" }}>{pr.label}</span>
                                        <span style={{ fontSize: 13, color: pr.active ? ACC : "var(--ink-500)" }}>{pr.sub}</span>
                                    </button>
                                ))}
                            </div>
                        }
                        <div ref={calRef} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", border: "1px solid var(--hair)", borderRadius: 8, background: "#fff", overflow: "hidden" }}>
                            {months.map(m => (
                                <div key={m.key} style={{ padding: "16px 16px 20px", borderLeft: "1px solid " + m.divider, overflow: "hidden" }}>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 36, marginBottom: 12 }}>
                                        <button type="button" onClick={() => shift(-1)} className="wpl-hs" style={{ width: 36, height: 36, borderRadius: "50%", border: "none", background: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", visibility: m.prevVis as any }}>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--ink-900)" strokeWidth="1.5"><path d="m15 18-6-6 6-6"></path></svg>
                                        </button>
                                        <span data-cal-anim="" style={{ fontSize: 16, fontWeight: 400, color: "var(--ink-900)" }}>{m.title}</span>
                                        <button type="button" onClick={() => shift(1)} className="wpl-hs" style={{ width: 36, height: 36, borderRadius: "50%", border: "none", background: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", visibility: m.nextVis as any }}>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--ink-900)" strokeWidth="1.5"><path d="m9 18 6-6-6-6"></path></svg>
                                        </button>
                                    </div>
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", marginBottom: 4 }}>
                                        {WK.map(w => <span key={w} style={{ height: 40, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "var(--ink-500)" }}>{w}</span>)}
                                    </div>
                                    <div data-cal-anim="" style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", rowGap: 4 }}>
                                        {m.cells.map((c: any, i: number) => (
                                            <div key={i} style={{ height: 40, display: "flex", alignItems: "center", justifyContent: "center", background: c.band, borderRadius: c.radius }}>
                                                <button type="button" onClick={c.onClick || undefined} disabled={c.off} className="wpl-hcal" style={{ pointerEvents: c.off ? "none" : "auto", flex: "none", width: "100%", maxWidth: 40, height: "auto", aspectRatio: "1 / 1", borderRadius: "50%", border: "1px solid " + c.border, background: c.dot, color: c.color, fontFamily: "inherit", fontSize: 14, fontWeight: c.weight, letterSpacing: "-0.02em", cursor: "pointer", padding: 0, visibility: c.vis, boxSizing: "border-box" }}>{c.day}</button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div style={col(10)}>
                        <label style={labelSt}>종료일*</label>
                        {renderDd(endDd)}
                    </div>

                    <div style={col(10)}>
                        <span style={labelSt}>등록 예정 기간</span>
                        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr) auto", alignItems: "center", gap: 12, width: "100%", minHeight: 48, padding: "0 16px", border: "1px solid var(--ink-200)", borderRadius: 8, background: "var(--bg-soft)", boxSizing: "border-box" }}>
                            <span style={{ minWidth: 0, fontSize: 15, fontWeight: 500, color: start ? "var(--ink-900)" : "var(--ink-300)", letterSpacing: "-0.02em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{start ? fmtLong(start) : "시작일 선택"}</span>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--ink-400)" strokeWidth="1.5"><path d="M5 12h14"></path><path d="m13 6 6 6-6 6"></path></svg>
                            <span style={{ minWidth: 0, fontSize: 15, fontWeight: 500, color: end ? "var(--ink-900)" : "var(--ink-300)", letterSpacing: "-0.02em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{end ? fmtLong(end) : "종료일 선택"}</span>
                            <span style={{ fontSize: 13, fontWeight: 500, lineHeight: 1, padding: "6px 10px", borderRadius: 4, background: "#E3F2FD", color: "#1976D2", visibility: start && end ? "visible" : "hidden" }}>총 {start && end ? dayNum(end) - dayNum(start) + 1 : 0}일</span>
                        </div>
                        {pastStart && <span style={{ fontSize: 13, lineHeight: 1.5, color: "#B42318" }}>시작일이 오늘보다 이전이에요. 소급 등록이 맞는지 확인해주세요.</span>}
                    </div>

                    {sep}

                    {benefits.length > 0 &&
                        <div style={col(14)}>
                            <span style={labelSt}>등록 혜택</span>
                            {benefits.map(bf => (
                                <button key={bf.id} type="button" role="checkbox" aria-checked={bf.on} aria-disabled={bf.disabled} onClick={bf.onClick} style={{ display: "flex", alignItems: "flex-start", gap: 12, background: "none", border: "none", padding: 0, cursor: bf.cursor, textAlign: "left", fontFamily: "inherit", letterSpacing: "-0.02em" }}>
                                    <span style={{ width: 20, height: 20, flex: "none", marginTop: 1, boxSizing: "border-box", border: "1px solid " + bf.boxBorder, borderRadius: 4, background: bf.fill, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" style={{ visibility: bf.on ? "visible" : "hidden" }}><path d="M20 6 9 17l-5-5"></path></svg>
                                    </span>
                                    <span style={col(4)}>
                                        <span style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                                            <span style={{ fontSize: 15, color: "var(--ink-900)" }}>{bf.label}</span>
                                            <span style={{ fontSize: 12, fontWeight: 500, lineHeight: 1, padding: "5px 8px", borderRadius: 4, border: "1px solid " + bf.badgeBorder, background: bf.badgeBg, color: bf.badgeColor }}>{bf.badge}</span>
                                            <span tabIndex={0} aria-label={bf.desc} onMouseEnter={bf.tipEnter} onMouseLeave={bf.tipLeave} onFocus={bf.tipEnter} onBlur={bf.tipLeave} onClick={e => { e.stopPropagation(); e.preventDefault(); }} className="wpl-hs" style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, borderRadius: "50%", cursor: "help", outline: "none" }}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7684" strokeWidth="2" strokeLinecap="round" style={{ display: "block" }}><circle cx="12" cy="12" r="9"></circle><path d="M12 11v5"></path><path d="M12 8h.01"></path></svg>
                                                {bf.tipOn && <span role="tooltip" style={{ position: "absolute", left: -6, bottom: "calc(100% + 8px)", zIndex: 60, width: "max-content", maxWidth: 260, padding: "8px 10px", borderRadius: 6, background: "var(--ink-900)", color: "#fff", fontSize: 13, fontWeight: 400, lineHeight: 1.5, letterSpacing: "-0.02em", textAlign: "left", whiteSpace: "normal", boxShadow: "0 6px 20px rgba(15, 23, 42, 0.18)", pointerEvents: "none" }}>{bf.desc}</span>}
                                            </span>
                                        </span>
                                    </span>
                                </button>
                            ))}
                            {warnings.map((w, i) => <span key={i} style={{ fontSize: 13, lineHeight: 1.5, color: "var(--ink-900)", paddingLeft: 32, textWrap: "pretty" } as CSSProperties}>주의 · {w}</span>)}
                        </div>
                    }

                    <div style={col(10)}>
                        <button type="button" onClick={() => set(p => ({ optOpen: !p.optOpen }))} aria-expanded={s.optOpen} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, width: "100%", height: 32, padding: 0, border: "none", background: "none", fontFamily: "inherit", cursor: "pointer", textAlign: "left" }}>
                            <span style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 }}>
                                <span style={labelSt}>추가 옵션</span>
                                {!s.optOpen && (s.editing || s.split) && <span style={{ fontSize: 13, color: "var(--ink-500)" }}>{[s.editing && "금액 직접 입력", s.split && "분할 결제"].filter(Boolean).join(" · ") + " 사용 중"}</span>}
                            </span>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ink-500)" strokeWidth="1.5" style={{ flex: "none", transform: s.optOpen ? "rotate(180deg)" : "none", transition: "transform 160ms" }}><path d="m6 9 6 6 6-6"></path></svg>
                        </button>
                        {s.optOpen && <>
                            <div style={grid2}>
                                {([["editing", "금액 직접 입력"], ["split", "분할 결제"]] as ["editing" | "split", string][]).map(([k, label]) => {
                                    const a = !!s[k];
                                    return (
                                        <button key={k} type="button" aria-pressed={a} className="wpl-hb" onClick={() => set(p => k === "editing"
                                            ? (p.editing ? { editing: false, manual: "" } : { editing: true })
                                            : { split: !p.split, splitN: 2, splitAuto: true, parts: [] })} style={segSt(a)}>{label}</button>
                                    );
                                })}
                            </div>
                            {s.editing &&
                                <span style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", alignItems: "center", gap: 8 }}>
                                    <input value={comma(digits(s.manual))} onChange={e => set({ manual: digits(e.target.value) })} inputMode="numeric" placeholder="총 결제 금액" style={{ ...inputSt, minWidth: 0, border: "1px solid " + (s.editing && !isManual ? INVALID : "var(--ink-200)"), textAlign: "right" }} />
                                    <span style={{ fontSize: 15, color: "var(--ink-900)" }}>원</span>
                                </span>
                            }
                            {s.split &&
                                <div style={{ display: "flex", flexDirection: "column", padding: "8px 20px", borderRadius: 8, background: "var(--bg-soft)" }}>
                                    {parts.map((v, i) => {
                                        const last = i === parts.length - 1;
                                        return (
                                            <div key={i} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 160px) auto 24px", alignItems: "center", gap: 6, minHeight: 52 }}>
                                                <span style={{ display: "flex", alignItems: "baseline", gap: 6, minWidth: 0 }}>
                                                    <span style={{ fontSize: 15, color: "var(--ink-500)" }}>{(i + 1) + "회차"}</span>
                                                    {last && <span style={{ fontSize: 13, color: "var(--ink-400)" }}>남은 금액</span>}
                                                </span>
                                                <input value={price == null ? "" : comma(digits(v))} disabled={last} inputMode="numeric" placeholder="0" className="wpl-part"
                                                    onChange={e => { if (last) return; const nv = digits(e.target.value); const a = heads.slice(); a[i] = nv; set({ parts: a, splitAuto: false }); }}
                                                    style={{ height: 36, width: "100%", minWidth: 0, padding: 0, border: "none", borderBottom: "1px solid " + (last ? "transparent" : "var(--ink-300)"), borderRadius: 0, outline: "none", fontFamily: "inherit", fontSize: 17, fontWeight: 600, color: price != null && partNums[i] <= 0 ? INVALID : "var(--ink-900)", letterSpacing: "-0.02em", background: "transparent", boxSizing: "border-box", textAlign: "right" }} />
                                                <span style={{ fontSize: 17, fontWeight: 600, color: "var(--ink-900)" }}>원</span>
                                                <button type="button" onClick={() => set({ splitN: Math.max(2, splitN - 1), splitAuto: true, parts: [] })} title="회차 삭제" aria-label="회차 삭제" className="wpl-hx" style={{ width: 24, height: 24, borderRadius: "50%", border: "none", background: "var(--ink-200)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, visibility: splitN > 2 ? "visible" : "hidden" }}>
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
                                                </button>
                                            </div>
                                        );
                                    })}
                                    <button type="button" onClick={() => set({ splitN: splitN + 1, splitAuto: true, parts: [] })} className="wpl-hadd" style={{ alignSelf: "flex-start", height: 44, padding: 0, border: "none", background: "none", color: "#1976D2", fontFamily: "inherit", fontSize: 15, fontWeight: 500, letterSpacing: "-0.02em", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14"></path><path d="M5 12h14"></path></svg>회차 추가
                                    </button>
                                </div>
                            }
                        </>}
                    </div>

                    {sep}

                    {enrolled ? (
                    <div style={col(10)}>
                        <span style={labelSt}>결제선생 기한 설정(d)</span>
                        <div style={col(10)}>
                            <span style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", alignItems: "center", gap: 8 }}>
                                <input value={s.days} onChange={e => { const v = digits(e.target.value).slice(0, 2); set({ days: v === "" ? "" : String(Number(v)) }); }} inputMode="numeric" style={{ ...inputSt, minWidth: 0, textAlign: "right" }} />
                                <span style={{ fontSize: 15, color: "var(--ink-900)", whiteSpace: "nowrap" }}>일</span>
                            </span>
                            {daysOk
                                ? <span style={{ fontSize: 13, lineHeight: 1.5, color: "var(--ink-500)" }}>{expTxt} 결제 가능</span>
                                : <span style={{ fontSize: 13, lineHeight: 1.5, color: INVALID }}>1~60일 사이로 입력해주세요</span>}
                        </div>
                    </div>
                    ) : (
                    <div style={col(10)}>
                        <span style={labelSt}>결제 만료시간(H)</span>
                        <div style={col(10)}>
                            <span style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", alignItems: "center", gap: 8 }}>
                                <input value={s.hours} onChange={e => { const v = digits(e.target.value).slice(0, 3); set({ hours: v === "" ? "" : String(Number(v)) }); }} inputMode="numeric" style={{ ...inputSt, minWidth: 0, textAlign: "right" }} />
                                <span style={{ fontSize: 15, color: "var(--ink-900)", whiteSpace: "nowrap" }}>시간</span>
                            </span>
                            {!hoursOk && <span style={{ fontSize: 13, lineHeight: 1.5, color: INVALID }}>1~720시간 사이로 입력해주세요</span>}
                        </div>
                    </div>
                    )}
                </div>

                {/* 하단 고정 바 */}
                <div style={{ flex: "none", borderTop: "1px solid var(--hair)", padding: "16px 32px 20px", display: "flex", flexDirection: "column", gap: 14, background: "#fff", overflowY: "hidden", scrollbarGutter: "stable" } as CSSProperties}>
                    <div aria-live="polite" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                            <span style={{ fontSize: 13, color: "var(--ink-500)" }}>등록 예정 비용</span>
                            <span style={{ fontSize: 14, color: "var(--ink-700)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{barPeriod}</span>
                        </div>
                        <span style={{ flex: "none", fontSize: 24, fontWeight: 700, color: "var(--ink-900)", letterSpacing: "-0.025em" }}>{price == null ? "-" : price.toLocaleString("ko-KR") + "원"}{enrolled && studentN > 1 && price != null ? <span style={{ fontSize: 14, fontWeight: 400, color: "var(--ink-500)" }}> × {studentN}명</span> : null}</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 2fr)", gap: 8 }}>
                        <button type="button" onClick={() => { if (stmt) set({ stmtOpen: true }); }} disabled={!stmt} title={stmt ? "" : "지점과 등록 기간을 선택하면 볼 수 있습니다"} className="wpl-hb" style={{ width: "100%", height: 48, borderRadius: 8, border: "1px solid var(--ink-200)", background: "#fff", color: stmt ? "var(--ink-900)" : "var(--ink-300)", fontFamily: "inherit", fontSize: 15, fontWeight: 500, letterSpacing: "-0.02em", cursor: stmt ? "pointer" : "default" }}>명세서 보기</button>
                        <button type="button" onClick={() => { if (ready) set({ confirmOpen: true }); }} disabled={!ready} style={{ width: "100%", height: 48, borderRadius: 8, border: "1px solid " + (ready ? ACC : "var(--bg-soft)"), background: ready ? ACC : "var(--bg-soft)", color: ready ? "#fff" : "var(--ink-300)", fontFamily: "inherit", fontSize: 15, fontWeight: 500, letterSpacing: "-0.02em", cursor: ready ? "pointer" : "default" }}>{sendLabel}</button>
                    </div>
                </div>
            </div>

            {/* 등록비 명세서 */}
            {s.stmtOpen && stmt &&
                <div onClick={() => set({ stmtOpen: false })} style={overlaySt(110)}>
                    <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 420, display: "flex", flexDirection: "column", gap: 12 }}>
                        <div data-print-root="" data-screen-label="등록비 명세서" style={{ ...subDialogSt, padding: "36px 24px 32px", display: "flex", flexDirection: "column", gap: 36 }}>
                            <h2 style={{ margin: 0, fontSize: 24, fontWeight: 300, lineHeight: 1.3, letterSpacing: "-0.025em", color: "var(--ink-900)" }}>등록비 명세서</h2>

                            <div style={{ display: "flex", flexDirection: "column" }}>
                                <span style={{ fontSize: 15, fontWeight: 500, color: "var(--ink-900)", paddingBottom: 12, borderBottom: "1px solid var(--ink-900)" }}>월 금액 기준</span>
                                {stmt.basis.map((r, i) => (
                                    <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 16, padding: "14px 0", borderBottom: "1px solid var(--hair)" }}>
                                        <span style={{ fontSize: 15, color: "var(--ink-700)" }}>{r.label}</span>
                                        <span style={{ fontSize: 15, color: "var(--ink-900)", textAlign: "right" }}>{r.value}</span>
                                    </div>
                                ))}
                            </div>

                            <div style={{ display: "flex", flexDirection: "column" }}>
                                <span style={{ fontSize: 15, fontWeight: 500, color: "var(--ink-900)", paddingBottom: 12, borderBottom: "1px solid var(--ink-900)" }}>월별 일할 계산 내역</span>
                                {stmt.rows.map((r, i) => (
                                    <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, padding: "14px 0", borderBottom: "1px solid var(--hair)" }}>
                                        <div style={col(4)}>
                                            <span style={{ fontSize: 15, color: "var(--ink-900)" }}>{r.month}</span>
                                            <span style={{ fontSize: 13, color: "var(--ink-500)" }}>{r.range}</span>
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
                                            <span style={{ fontSize: 15, color: "var(--ink-900)" }}>{r.amount}</span>
                                            <span style={{ fontSize: 13, color: "var(--ink-500)" }}>{r.days}</span>
                                        </div>
                                    </div>
                                ))}
                                {stmt.totals.map((t, i) => (
                                    <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 16, padding: "18px 0 0" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            <span style={{ fontSize: 15, fontWeight: t.weight, color: "var(--ink-900)" }}>{t.label}</span>
                                            {t.manual && <span style={{ fontSize: 13, fontWeight: 500, color: "var(--ink-900)", border: "1px solid var(--ink-900)", borderRadius: 4, padding: "2px 8px", lineHeight: 1.4 }}>수동</span>}
                                        </div>
                                        <span style={{ fontSize: t.size, fontWeight: 300, color: t.color, letterSpacing: "-0.025em", textDecoration: t.strike }}>{t.value}</span>
                                    </div>
                                ))}
                            </div>

                            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: "var(--ink-500)", textWrap: "pretty" } as CSSProperties}>{stmt.note}</p>
                        </div>
                        <div data-print-hide="" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 2fr)", gap: 8 }}>
                            <button type="button" onClick={() => set({ stmtOpen: false })} className="wpl-hb" style={ghostBtnSt}>닫기</button>
                            <button type="button" onClick={() => window.print()} className="wpl-hblue" style={blueBtnSt}>PDF 저장 · 인쇄</button>
                        </div>
                    </div>
                </div>
            }

            {/* 발송 전 확인 */}
            {s.confirmOpen && !missing &&
                <div onClick={() => { if (!sending) set({ confirmOpen: false }); }} style={overlaySt(120)}>
                    <div role="dialog" aria-label="발송 전 확인" data-screen-label="발송 전 확인" onClick={e => e.stopPropagation()} style={{ ...subDialogSt, width: "100%", maxWidth: 420, padding: "36px 24px 24px", display: "flex", flexDirection: "column", gap: 24 }}>
                        <div style={col(8)}>
                            <h2 style={{ margin: 0, fontSize: 24, fontWeight: 300, lineHeight: 1.3, letterSpacing: "-0.025em", color: "var(--ink-900)" }}>이대로 링크를 보낼까요?</h2>
                            <span style={{ fontSize: 14, lineHeight: 1.5, color: "var(--ink-500)" }}>번호와 금액을 한 번 더 확인해주세요.</span>
                        </div>
                        {!!dup &&
                            <div role="alert" style={{ padding: "12px 16px", borderRadius: 8, background: "#FDECEA", fontSize: 13, lineHeight: 1.5, color: "#B42318" }}>
                                {"이 번호로 " + pad2(new Date(dup.at).getHours()) + ":" + pad2(new Date(dup.at).getMinutes()) + "에 이미 보냈어요. 다시 보내면 링크가 2개가 됩니다."}
                            </div>
                        }
                        <div style={{ display: "flex", flexDirection: "column", borderTop: "1px solid var(--ink-900)" }}>
                            {confirmRows.map(([label, value]) => (
                                <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 16, padding: "12px 0", borderBottom: "1px solid var(--hair)" }}>
                                    <span style={{ flex: "none", fontSize: 14, color: "var(--ink-500)" }}>{label}</span>
                                    <span style={{ fontSize: 15, color: "var(--ink-900)", textAlign: "right", textWrap: "pretty" } as CSSProperties}>{value}</span>
                                </div>
                            ))}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 16, padding: "18px 0 0" }}>
                                <span style={{ fontSize: 15, fontWeight: 500, color: "var(--ink-900)" }}>결제 금액</span>
                                <span style={{ fontSize: 24, fontWeight: 700, color: "var(--ink-900)", letterSpacing: "-0.025em" }}>{price == null ? "-" : (s.split ? won(price) + " (" + splitN + "회 분할)" : won(price)) + (enrolled ? " × " + studentN + "명" : "")}</span>
                            </div>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 2fr)", gap: 8 }}>
                            <button type="button" onClick={() => set({ confirmOpen: false })} disabled={sending} className="wpl-hb" style={ghostBtnSt}>고치기</button>
                            <button type="button" onClick={doSend} disabled={sending} className="wpl-hblue" style={{ ...blueBtnSt, opacity: sending ? 0.6 : 1, cursor: sending ? "default" : "pointer" }}>{sending ? "보내는 중..." : "보내기"}</button>
                        </div>
                    </div>
                </div>
            }

            {/* 재학생 모드: 재원생 고르기 */}
            {enrolled &&
                <WinterEnrolledPickerModal
                    open={s.pickerOpen}
                    selected={s.students}
                    onCancel={() => set({ pickerOpen: false })}
                    onConfirm={(list) => set(p => ({ pickerOpen: false, students: list, moreOpen: false, seat: list.length >= 2 ? "" : p.seat }))}
                />
            }

            {/* 재학생 모드: 발송 결과 (제외·실패 학생이 있을 때) */}
            {enrolled && sendResult &&
                <div style={overlaySt(130)}>
                    <div role="dialog" aria-label="발송 결과" onClick={e => e.stopPropagation()} style={{ ...subDialogSt, width: "100%", maxWidth: 460, padding: "32px 24px 24px", display: "flex", flexDirection: "column", gap: 20 }}>
                        <h2 style={{ margin: 0, fontSize: 24, fontWeight: 300, lineHeight: 1.3, color: "var(--ink-900)" }}>발송 결과</h2>
                        <div style={{ fontSize: 15, color: "var(--ink-900)" }}>
                            보냄 {sendResult.sent.length}명 · 제외 {sendResult.skipped.length}명 · 실패 {sendResult.failed.length}명
                        </div>
                        {[["제외된 학생", sendResult.skipped], ["실패한 학생", sendResult.failed]].map(([title, list]: any) => list.length > 0 &&
                            <div key={title} style={col(6)}>
                                <span style={{ fontSize: 14, fontWeight: 500, color: "var(--ink-700)" }}>{title}</span>
                                {list.map((x: any, i: number) => (
                                    <span key={i} style={{ fontSize: 14, lineHeight: 1.5, color: "var(--ink-900)" }}>
                                        {(x.location || "") + (x.seat ? "(" + x.seat + "번)" : "") + " " + (x.name || "학생 " + x.userId)} — <span style={{ color: INVALID }}>{x.reason}</span>
                                    </span>
                                ))}
                            </div>
                        )}
                        <button type="button" onClick={() => { setSendResult(null); props.handleClose(); }} className="wpl-hblue" style={blueBtnSt}>닫기</button>
                    </div>
                </div>
            }
        </>
    );
};

export default WinterPayLinkModal;
